import { Container, Sprite, type Texture } from "pixi.js";
import type { Log, PresentationTicker } from "@slot/presentation";

/**
 * Grid-area overlay shown when a network roundtrip (spin response or presentation-complete
 * settlement) is taking longer than expected: a 150² sprite cycling the `loadingAnimation`
 * frames at `framesPerSecond`, ticked by the presentation ticker (so the dev pause freezes it).
 * The presenter decides when to show / hide.
 */
export class GridLoadingOverlayView extends Container {
  private readonly log: Log;
  private readonly sprite: Sprite;
  private readonly frames: readonly Texture[];
  private readonly framesPerSecond: number;
  private readonly ticker: PresentationTicker;
  private unsubscribe: (() => void) | null = null;
  private currentFrameIndex = 0;
  private timeAccumulator = 0;

  public constructor(input: { frames: readonly Texture[]; ticker: PresentationTicker; framesPerSecond?: number; log: Log }) {
    super({ label: "GridLoadingOverlay" });
    this.log = input.log;
    this.frames = input.frames;
    this.ticker = input.ticker;
    this.framesPerSecond = input.framesPerSecond ?? 30;
    this.sprite = new Sprite({ texture: this.frames[0], label: "LoadingSprite" });
    this.sprite.anchor.set(0.5);
    this.sprite.width = 150;
    this.sprite.height = 150;
    this.addChild(this.sprite);
    this.hide();
  }

  public show(): void {
    if (this.unsubscribe) return;
    if (this.frames.length === 0) {
      this.log("[GridLoadingOverlay] no frames wired; show() is a no-op.");
      return;
    }
    this.visible = true;
    // Reset to frame 0 each time so a brief hide → show looks deliberate.
    this.currentFrameIndex = 0;
    this.timeAccumulator = 0;
    this.applyFrame(0);
    this.unsubscribe = this.ticker.add((dt) => this.update(dt));
  }

  public hide(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.visible = false;
  }

  public isShowing(): boolean {
    return this.unsubscribe !== null;
  }

  private update(dt: number): void {
    const frameDuration = 1 / Math.max(1, this.framesPerSecond);
    this.timeAccumulator += dt;
    // Advance several frames after a long stall rather than slowing the animation.
    while (this.timeAccumulator >= frameDuration) {
      this.timeAccumulator -= frameDuration;
      this.currentFrameIndex = (this.currentFrameIndex + 1) % this.frames.length;
      this.applyFrame(this.currentFrameIndex);
    }
  }

  private applyFrame(index: number): void {
    const frame = this.frames[index];
    if (frame) {
      this.sprite.texture = frame;
      this.sprite.width = 150;
      this.sprite.height = 150;
    }
  }
}
